///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { createSlice } from "@reduxjs/toolkit";
import {
  cloneDeep,
  find,
  findIndex,
  orderBy,
  sortBy,
  uniq,
  uniqBy
} from "lodash";
import { differenceInDays, parseISO } from "date-fns";

import {
  acceptBidModeCapacityMatch,
  getCapacityMatchLanesSummary,
  getCarrierMatches,
  getIsCapacityMatchingInProgress,
  getMatchingRunDate,
  runInternalCapacityMatching,
  submitBidModeCapacityMatch,
  updateCapacityMatchingStatus,
  upsertLaneCarrierCapacityMatches,
  getRateHistory,
} from "../api/capacityMatch";
import { equipments, order as orderTypes } from "../helpers/types";
import { fetchBidMessages, fetchBidSummary } from "./bidSummarySlice";
import mapLaneCarrierCapacityMatch from "../helpers/mapLaneCarrierCapacityMatch";
import getCommonCarrierMatches from "../helpers/getCommonCarrierMatches";
import filterCapacityMatches from "../helpers/filterCapacityMatches";
import pricingSliceHelper from "../helpers/pricingSliceHelper";
import capacityMatchLaneSummaryColumns from "../helpers/capacityMatchLaneSummaryColumns";

export const initialState = {
  capacityMatchLaneSummary: {
    isFetching: false,
    isError: false,
    capacityMatches: null,
    sortField: "",
    sortOrder: "",
    columnWidths: {},
    offset: 0,
    perPage: 50,
    totalFiltered: 0,
    columnFilterMenu: { field: null, visible: false },
    columnFilters: {},
    rangeFilters: {},
    filtersApplied: false,
    columns: {
      changed: false,
      order: []
    }
  },
  acceptCapacityMatchIsLoading: false,
  acceptCapacityMatchFailure: false,
  submitCapacityMatchIsLoading: false,
  submitCapacityMatchFailure: false,
  updateCapacityMatchStatusIsLoading: false,
  updateCapacityMatchStatusFailure: false,
  rowsSelected: [],
  copyMatchesFromRow: null,
  capacityMatchCarriers: {
    isFetching: false,
    isError: false,
    carriers: null,
    originalCarriers: null,
    laneMatches: null,
    noneOption: false,
    unchangedValues: {
      laneMatches: null,
      noneOption: false
    }
  },
  saveCarrierMatchDetails: {
    isFetching: false,
    isError: false
  },
  isCapacityMatchingInProgress: false,
  refreshCapacityMatchesIsLoading: false,
  matchesLastRanBefore7Days: false,
  isBulkMatchingOperationEnabled: false,
  step: 1,
  rateHistory: {
      isFetching: false,
      isError: false,
      rateHistories: null
  },
};

const bidProcurementSlice = createSlice({
  name: "bidProcurement",
  initialState,
  reducers: {
    requestCapacityMatchLaneSummary: (
      state,
      { payload: { clearFilterAndSortDetails } }
    ) => {
      state.capacityMatchLaneSummary.isFetching = true;
      state.capacityMatchLaneSummary.isError = false;
      state.capacityMatchLaneSummary.capacityMatches = null;
      state.capacityMatchLaneSummary.totalFiltered = 0;
      state.rowsSelected = [];
      state.copyMatchesFromRow = null;
      state.capacityMatchLaneSummary.columnFilterMenu = {
        field: null,
        visible: false
      };
      state.capacityMatchLaneSummary.filtersApplied = false;
      if (clearFilterAndSortDetails) {
        state.capacityMatchLaneSummary.sortField = "";
        state.capacityMatchLaneSummary.sortOrder = "";
        state.capacityMatchLaneSummary.offset = 0;
        state.capacityMatchLaneSummary.perPage = 50;
        state.capacityMatchLaneSummary.columnFilters = {};
        state.capacityMatchLaneSummary.rangeFilters = {};
      }
    },
    receiveCapacityMatchLaneSummarySuccess: (state, { payload }) => {
      state.capacityMatchLaneSummary.isFetching = false;
      state.capacityMatchLaneSummary.isError = false;
      state.capacityMatchLaneSummary.capacityMatches = payload.map(
        (capacityMatches, index) => ({
          ...capacityMatches.lane,
          ...capacityMatches.capacityMatchSummary,
          groupsCount: capacityMatches.capacityMatchSummary.groups?.length,
          procurementRepNames: uniq(capacityMatches.laneCarrierCapacityMatchAuditUsers?.map(c => 
            c.userName
          )),
          carrierNamesCount:
            capacityMatches.capacityMatchSummary.carrierNames?.length,
          repNamesCount: capacityMatches.capacityMatchSummary.repNames?.length,
          equipmentType: equipments[capacityMatches.lane.equipmentType],
          laneMatches: capacityMatches.carrierMatches,
          metaData: { matched: true },
          defaultIndex: index
        })
      );
      state.capacityMatchLaneSummary.totalFiltered = payload.length;
      state.rowsSelected = [];
      state.copyMatchesFromRow = null;
    },
    receiveCapacityMatchLaneSummaryFailure: state => {
      state.capacityMatchLaneSummary.isFetching = false;
      state.capacityMatchLaneSummary.isError = true;
      state.capacityMatchLaneSummary.capacityMatches = null;
      state.capacityMatchLaneSummary.totalFiltered = 0;
      state.rowsSelected = [];
      state.copyMatchesFromRow = null;
    },
    sortCapacityMatchLaneSummary: (
      state,
      { payload: { order, field, useSortDetailsFromStore } }
    ) => {
      if (!useSortDetailsFromStore) {
        state.capacityMatchLaneSummary.sortOrder = order;
        state.capacityMatchLaneSummary.sortField = field;
        state.capacityMatchLaneSummary.offset = 0;
      }
      const { sortField, sortOrder } = state.capacityMatchLaneSummary;
      state.capacityMatchLaneSummary.capacityMatches = orderBy(
        state.capacityMatchLaneSummary.capacityMatches,
        [sortField ? sortField : "defaultIndex"],
        [sortOrder ? sortOrder : orderTypes.ascending]
      );
    },
    setColumnWidth: (state, { payload: { width, field } }) => {
      state.capacityMatchLaneSummary.columnWidths[field] = width;
    },
    requestAcceptCapacityMatch: state => {
      state.acceptCapacityMatchIsLoading = true;
      state.acceptCapacityMatchFailure = false;
    },
    receiveAcceptCapacityMatchSuccess: state => {
      state.acceptCapacityMatchIsLoading = false;
      state.acceptCapacityMatchFailure = false;
    },
    receiveAcceptCapacityMatchFailure: state => {
      state.acceptCapacityMatchIsLoading = false;
      state.acceptCapacityMatchFailure = true;
    },
    requestSubmitCapacityMatch: state => {
      state.submitCapacityMatchIsLoading = true;
      state.submitCapacityMatchFailure = false;
    },
    receiveSubmitCapacityMatchSuccess: state => {
      state.submitCapacityMatchIsLoading = false;
      state.submitCapacityMatchFailure = false;
    },
    receiveSubmitCapacityMatchFailure: state => {
      state.submitCapacityMatchIsLoading = false;
      state.submitCapacityMatchFailure = true;
    },
    requestUpdateCapacityMatchStatus: state => {
      state.updateCapacityMatchStatusIsLoading = true;
      state.updateCapacityMatchStatusFailure = false;
    },
    receiveUpdateCapacityMatchStatusSuccess: state => {
      state.updateCapacityMatchStatusIsLoading = false;
      state.updateCapacityMatchStatusFailure = false;
    },
    receiveUpdateCapacityMatchStatusFailure: state => {
      state.updateCapacityMatchStatusIsLoading = false;
      state.updateCapacityMatchStatusFailure = true;
    },
    resetBannerActionState: state => {
      state.acceptCapacityMatchIsLoading = false;
      state.acceptCapacityMatchFailure = false;
      state.submitCapacityMatchIsLoading = false;
      state.submitCapacityMatchFailure = false;
      state.updateCapacityMatchStatusIsLoading = false;
      state.updateCapacityMatchStatusFailure = false;
    },
    setRowsSelected: (state, { payload }) => {
      let rows = state.rowsSelected;
      if (rows.some(x => x === payload)) {
        rows = rows.filter(x => x !== payload);
      } else {
        rows.push(payload);
      }
      state.rowsSelected = rows;
    },
    clearRowsSelected: state => {
      state.rowsSelected = [];
    },
    setAllRowsSelected: state => {
      let copiedRow = state.copyMatchesFromRow;
      state.rowsSelected = state.capacityMatchLaneSummary.capacityMatches
        .filter(y => y.truckloadLaneId !== copiedRow)
        .map(x => x.truckloadLaneId);
    },
    setCopyMatchesFromRow: (state, { payload }) => {
      state.copyMatchesFromRow = payload;
    },
    requestCarrierMatches: state => {
      state.capacityMatchCarriers.isFetching = true;
      state.capacityMatchCarriers.isError = false;
      state.capacityMatchCarriers.carriers = null;
      state.capacityMatchCarriers.laneMatches = null;
      state.capacityMatchCarriers.noneOption = false;
      state.capacityMatchCarriers.unchangedValues.laneMatches = null;
      state.capacityMatchCarriers.unchangedValues.noneOption = false;
    },
    receiveCarrierMatchesSuccess: (
      state,
      { payload: { commonCarrierMatches, carrierMatches } }
    ) => {
      state.capacityMatchCarriers.isFetching = false;
      state.capacityMatchCarriers.isError = false;
      state.capacityMatchCarriers.carriers = commonCarrierMatches.map(c => ({
        ...c,
        checked: false
      }));
      state.capacityMatchCarriers.originalCarriers = carrierMatches;
      state.capacityMatchCarriers.laneMatches = null;
      state.capacityMatchCarriers.noneOption = false;
      state.capacityMatchCarriers.unchangedValues.laneMatches = null;
      state.capacityMatchCarriers.unchangedValues.noneOption = false;
    },
    receiveCarrierMatchesFailure: state => {
      state.capacityMatchCarriers.isFetching = false;
      state.capacityMatchCarriers.isError = true;
      state.capacityMatchCarriers.carriers = null;
      state.capacityMatchCarriers.laneMatches = null;
      state.capacityMatchCarriers.noneOption = false;
      state.capacityMatchCarriers.unchangedValues.laneMatches = null;
      state.capacityMatchCarriers.unchangedValues.noneOption = false;
    },
    setCarriersSelected: (state, { payload }) => {
      let carriersList = state.capacityMatchCarriers.carriers;
      carriersList?.forEach(c => {
        if (c.carrierId === payload) {
          c.checked = !c.checked;
        }
      });
      state.capacityMatchCarriers.carriers = carriersList;
    },
    clearCarriersSelected: state => {
      let carriersList = state.capacityMatchCarriers.carriers;
      carriersList?.forEach(c => {
        c.checked = false;
      });
      state.capacityMatchCarriers.carriers = carriersList;
    },
    addNewCarrierMatches: (state, { payload }) => {
      let carriersList = state.capacityMatchCarriers.carriers;
      if (!carriersList?.some(x => x.carrierId === payload.id)) {
        carriersList.unshift({
          ...payload,
          carrierId: payload.id,
          checked: true
        });
      } else {
        carriersList?.forEach(c => {
          if (c.carrierId === payload.id) {
            c.checked = true;
          }
        });
      }
      state.capacityMatchCarriers.carriers = carriersList;
    },
    createLaneMatches: (state, { payload: { truckloadLaneIds, carriers } }) => {
      let newLaneMatches = [];
      const existingLaneMatches = state.capacityMatchCarriers.laneMatches;
      const selectedCarriers = carriers?.filter(x => x.checked === true);

      truckloadLaneIds?.forEach(truckloadLaneId => {
        selectedCarriers?.forEach(carrier => {
          let existingLaneMatch = existingLaneMatches?.find(
            x =>
              x.carrierId === carrier.carrierId &&
              x.truckloadLaneId === truckloadLaneId
          );
          newLaneMatches.push({
            carrierId: carrier.carrierId,
            carrierName: carrier.carrierName,
            carrierCode: carrier.carrierCode,
            incumbent: carrier.incumbent,
            checked: carrier.checked,
            notes: "",
            ...existingLaneMatch,
            truckloadLaneId
          });
        });
      });

      state.capacityMatchCarriers.laneMatches = newLaneMatches;
    },
    updateLaneMatches: (
      state,
      { payload: { truckloadLaneIds, carrierId, field, value } }
    ) => {
      truckloadLaneIds.forEach(truckloadLaneId => {
        let laneMatches = state.capacityMatchCarriers.laneMatches;
        let laneMatchIndex = findIndex(
          laneMatches,
          x =>
            x.truckloadLaneId === truckloadLaneId && x.carrierId === carrierId
        );
        laneMatches[laneMatchIndex] = {
          ...laneMatches[laneMatchIndex],
          [field]: value
        };
        state.capacityMatchCarriers.laneMatches = laneMatches;
      });
    },
    setNoneOption: (state, { payload }) => {
      state.capacityMatchCarriers.noneOption = payload;
    },
    setCapacityMatchCarriersInfo: (
      state,
      { payload: { laneMatches, isNoneOptionSelected, isCopyMatch = false } }
    ) => {
      let carriersList = state.capacityMatchCarriers.carriers;
      laneMatches.forEach(laneMatch => {
        if (laneMatch.carrierId !== null) {
          let carrierIndex = findIndex(
            carriersList,
            x => x.carrierId === laneMatch.carrierId
          );
          if (carrierIndex >= 0) {
            carriersList[carrierIndex] = {
              ...carriersList[carrierIndex],
              checked: true
            };
          } else {
            carriersList?.unshift({
              carrierId: laneMatch.carrierId,
              carrierName: laneMatch.carrierName,
              carrierCode: laneMatch.carrierCode,
              incumbent: laneMatch.incumbent,
              checked: true
            });
          }
        }
      });

      state.capacityMatchCarriers.carriers = carriersList;
      state.capacityMatchCarriers.laneMatches = laneMatches;
      state.capacityMatchCarriers.noneOption = isNoneOptionSelected;
      if (!isCopyMatch) {
        state.capacityMatchCarriers.unchangedValues.laneMatches = laneMatches;
        state.capacityMatchCarriers.unchangedValues.noneOption =
          isNoneOptionSelected;
      }
    },
    setSaveCarrierMatchDetails: (
      state,
      { payload: { isFetching, isError } }
    ) => {
      state.saveCarrierMatchDetails.isFetching = isFetching;
      state.saveCarrierMatchDetails.isError = isError;
    },
    updateStep: (state, { payload }) => {
      state.step = payload;
    },
    changeCapacityMatchesPagination: (
      state,
      { payload: { offset, limit } }
    ) => {
      state.capacityMatchLaneSummary.offset = offset;
      state.capacityMatchLaneSummary.perPage = limit;
    },
    setCapacityMatchingInProgress: (state, { payload }) => {
      state.isCapacityMatchingInProgress = payload;
    },
    setRefreshCapacityMatchesIsLoading: (state, { payload }) => {
      state.refreshCapacityMatchesIsLoading = payload;
    },
    toggleColumnFilterMenu: (
      state,
      { payload: { field, visible, filter, multiValuedColumn } }
    ) => {
      state.capacityMatchLaneSummary.columnFilterMenu = { field, visible };

      const columnFilters = state.capacityMatchLaneSummary.columnFilters[field];
      if (filter) {
        if (visible) {
          const stateCopy = cloneDeep(state);
          if (stateCopy.capacityMatchLaneSummary.columnFilters[field]) {
            stateCopy.capacityMatchLaneSummary.columnFilters[field] = [];
          }
          filterCapacityMatches(stateCopy, {
            payload: {
              applyOnExistingFilters: true
            }
          });
          let filterOptions = uniq(
            stateCopy.capacityMatchLaneSummary.capacityMatches
              .filter(l => l.metaData.matched)
              .map(l => l[field])
          );

          if (multiValuedColumn) {
            let values = [];
            let index = 0;
            filterOptions.map(v => {
              v.forEach(y => {
                values.push({
                  key: index + 1,
                  label: y === undefined || y === null ? "" : y,
                  value: y
                });
                index++;
              });
              return {};
            });
            filterOptions = uniqBy(values, v => v.value);
          } else {
            filterOptions = filterOptions.map((v, i) => ({
              key: i + 1,
              label: v === undefined || v === null ? "" : v,
              value: v
            }));
          }

          state.capacityMatchLaneSummary.columnFilters[field] = sortBy(
            filterOptions.map(f => {
              const option =
                columnFilters && columnFilters.find(o => o.value === f.value);
              return { ...f, selected: option && option.selected };
            }),
            f => {
              return f.label.toString().toLowerCase();
            }
          );
        } else {
          state.capacityMatchLaneSummary.columnFilters[field] =
            columnFilters && columnFilters.filter
              ? columnFilters.filter(f => f.selected)
              : [];
        }
      }
    },
    filterCapacityMatchesSummary: filterCapacityMatches,
    resetCapacityMatchesSummaryFilters: state => {
      state.capacityMatchLaneSummary.filtersApplied = false;
      state.capacityMatchLaneSummary.columnFilters =
        initialState.capacityMatchLaneSummary.columnFilters;
      state.capacityMatchLaneSummary.rangeFilters =
        initialState.capacityMatchLaneSummary.rangeFilters;
      state.capacityMatchLaneSummary.columnFilterMenu =
        initialState.capacityMatchLaneSummary.columnFilterMenu;
      if (state.capacityMatchLaneSummary.capacityMatches?.length > 0) {
        state.capacityMatchLaneSummary.capacityMatches.forEach(
          matches => (matches.metaData.matched = true)
        );
        state.capacityMatchLaneSummary.totalFiltered =
          state.capacityMatchLaneSummary.capacityMatches.length;
      }
    },
    loadColumnOrder: state => {
      let columns = capacityMatchLaneSummaryColumns.map(c => c.fieldName);

      pricingSliceHelper.loadColumns(
        state.capacityMatchLaneSummary,
        columns,
        localStorage.capacityMatchLaneSummaryColumnOrder
      );
    },
    setColumnOrder: (state, action) => {
      pricingSliceHelper.setColumnOrder(
        state.capacityMatchLaneSummary,
        action,
        capacityMatchLaneSummaryColumns.map(c => c.fieldName)
      );
      localStorage.capacityMatchLaneSummaryColumnOrder = JSON.stringify(
        state.capacityMatchLaneSummary.columns.order
      );
    },
    resetColumnOrder: state => {
      state.capacityMatchLaneSummary.columns.order =
        capacityMatchLaneSummaryColumns.map(c => c.fieldName);
      state.capacityMatchLaneSummary.columns.changed = false;
      localStorage.removeItem("capacityMatchLaneSummaryColumnOrder");
    },
    setMatchesLastRanBefore7Days: (state, { payload }) => {
      state.matchesLastRanBefore7Days = payload;
    },
    receiveCapacityMatchLaneSummarySuccessForEditedLanes: (
      state,
      { payload }
    ) => {
      let previouslyFetchedLaneSummary =
        state.capacityMatchLaneSummary.capacityMatches;
      payload.forEach((capacityMatches, i) => {
        const index = findIndex(previouslyFetchedLaneSummary, {
          truckloadLaneId: capacityMatches.lane.truckloadLaneId
        });
        const previousCapacityMatch = find(previouslyFetchedLaneSummary, {
          truckloadLaneId: capacityMatches.lane.truckloadLaneId
        });

        let capacityMatch = {
          ...previousCapacityMatch,
          ...capacityMatches.lane,
          ...capacityMatches.capacityMatchSummary,
          groupsCount: capacityMatches.capacityMatchSummary.groups?.length,
          procurementRepNames: uniq(capacityMatches.laneCarrierCapacityMatchAuditUsers?.map(c => 
            c.userName
          )),
          carrierNamesCount:
            capacityMatches.capacityMatchSummary.carrierNames?.length,
          repNamesCount: capacityMatches.capacityMatchSummary.repNames?.length,
          equipmentType: equipments[capacityMatches.lane.equipmentType],
          laneMatches: capacityMatches.carrierMatches
        };
        previouslyFetchedLaneSummary.splice(index, 1, capacityMatch);
      });
      state.capacityMatchLaneSummary.capacityMatches =
        previouslyFetchedLaneSummary;
    },
    enableBulkMatchingOperation: (state, { payload }) => {
      state.isBulkMatchingOperationEnabled = payload;
    },
    requestRateHistory: state => {
        state.rateHistory.isFetching = true;
        state.rateHistory.isError = false;
        state.rateHistory.rateHistories = null;
    },
    receiveRateHistorySuccess: (
        state,
        { payload: { rateHistory } }
    ) => {
        state.rateHistory.isFetching = false;
        state.rateHistory.isError = false;
        state.rateHistory.rateHistories = rateHistory;
    },
    receiveRateHistoryFailure: state => {
        state.rateHistory.isFetching = false;
        state.rateHistory.isError = true;
        state.rateHistory.rateHistories = null;
    },
    resetState: () => initialState
  }
});

export const {
  requestCapacityMatchLaneSummary,
  receiveCapacityMatchLaneSummarySuccess,
  receiveCapacityMatchLaneSummaryFailure,
  sortCapacityMatchLaneSummary,
  setColumnWidth,
  requestAcceptCapacityMatch,
  receiveAcceptCapacityMatchSuccess,
  receiveAcceptCapacityMatchFailure,
  requestSubmitCapacityMatch,
  receiveSubmitCapacityMatchSuccess,
  receiveSubmitCapacityMatchFailure,
  requestUpdateCapacityMatchStatus,
  receiveUpdateCapacityMatchStatusSuccess,
  receiveUpdateCapacityMatchStatusFailure,
  resetBannerActionState,
  setRowsSelected,
  clearRowsSelected,
  setAllRowsSelected,
  setCopyMatchesFromRow,
  requestCarrierMatches,
  receiveCarrierMatchesSuccess,
  receiveCarrierMatchesFailure,
  setCarriersSelected,
  clearCarriersSelected,
  addNewCarrierMatches,
  createLaneMatches,
  updateLaneMatches,
  setNoneOption,
  setCapacityMatchCarriersInfo,
  setSaveCarrierMatchDetails,
  updateStep,
  changeCapacityMatchesPagination,
  setCapacityMatchingInProgress,
  setRefreshCapacityMatchesIsLoading,
  toggleColumnFilterMenu,
  filterCapacityMatchesSummary,
  resetCapacityMatchesSummaryFilters,
  loadColumnOrder,
  setColumnOrder,
  resetColumnOrder,
  setMatchesLastRanBefore7Days,
  receiveCapacityMatchLaneSummarySuccessForEditedLanes,
  enableBulkMatchingOperation,
  requestRateHistory,
  receiveRateHistorySuccess,
  receiveRateHistoryFailure,
  resetState
} = bidProcurementSlice.actions;

export const bidProcurementReducer = bidProcurementSlice.reducer;

export const fetchCapacityMatchLaneSummary =
  (bidId, clearFilterAndSortDetails = true) =>
  async (dispatch, getState) => {
    const {
      user: { user },
      featureFlags: { enableExportMatches }
    } = getState();

    dispatch(requestCapacityMatchLaneSummary({ clearFilterAndSortDetails }));
    dispatch(loadColumnOrder());

    try {
      let lanesSummary = (
        await getCapacityMatchLanesSummary(
          bidId,
          user.userRoleForManagementUser
        )
      ).data;
      dispatch(receiveCapacityMatchLaneSummarySuccess(lanesSummary));
      enableExportMatches && dispatch(getMatchingLastRunDate(bidId));
      if (clearFilterAndSortDetails === false) {
        dispatch(
          filterCapacityMatchesSummary({ applyOnExistingFilters: true })
        );
        dispatch(
          sortCapacityMatchLaneSummary({ useSortDetailsFromStore: true })
        );
      }
    } catch {
      dispatch(receiveCapacityMatchLaneSummaryFailure());
    }
  };

export const acceptCapacityMatch =
  (bidModeId, bidId, role, route) => async (dispatch, getState) => {
    const { user } = getState().user;
    dispatch(requestAcceptCapacityMatch());

    try {
      await acceptBidModeCapacityMatch(
        bidId,
        bidModeId,
        user.userRoleForManagementUser
      );
      dispatch(receiveAcceptCapacityMatchSuccess());
      dispatch(checkCapacityMatchingInProgress(bidId));
      dispatch(fetchBidSummary({ bidId, role }));
      route === "summary" && dispatch(fetchBidMessages(bidId));
      route === "procurement" && dispatch(fetchCapacityMatchLaneSummary(bidId));
    } catch {
      dispatch(receiveAcceptCapacityMatchFailure());
    }
  };

export const submitCapacityMatch =
  (bidModeId, bidId, role, route) => async (dispatch, getState) => {
    const { user } = getState().user;
    dispatch(requestSubmitCapacityMatch());

    try {
      await submitBidModeCapacityMatch(
        bidId,
        bidModeId,
        user.userRoleForManagementUser
      );
      dispatch(receiveSubmitCapacityMatchSuccess());
      dispatch(fetchBidSummary({ bidId, role }));
      route === "summary" && dispatch(fetchBidMessages(bidId));
      route === "procurement" && dispatch(fetchCapacityMatchLaneSummary(bidId));
    } catch {
      dispatch(receiveSubmitCapacityMatchFailure());
    }
  };

export const fetchCarrierMatches =
  (bidId, callbackFn, isCopyMatch) => async (dispatch, getState) => {
    const {
      user: { user },
      bidProcurement: { rowsSelected, copyMatchesFromRow }
    } = getState();
    dispatch(requestCarrierMatches());

    try {
      let carrierMatches = (
        await getCarrierMatches(
          isCopyMatch ? [copyMatchesFromRow] : rowsSelected,
          bidId,
          user.userRoleForManagementUser
        )
      ).data;
      const commonCarrierMatches = getCommonCarrierMatches(
        carrierMatches,
        isCopyMatch ? [copyMatchesFromRow] : rowsSelected
      );
      dispatch(
        receiveCarrierMatchesSuccess({ commonCarrierMatches, carrierMatches })
      );
      callbackFn && callbackFn();
    } catch {
      dispatch(receiveCarrierMatchesFailure());
    }
  };

export const saveCarrierMatches =
  (
    bidId,
    isNoneOptionSelected,
    callbackFn,
    clearExistingMatches,
    updatedLaneMatches
  ) =>
  async (dispatch, getState) => {
    const {
      user: { user },
      bidProcurement: {
        capacityMatchLaneSummary: { capacityMatches },
        capacityMatchCarriers: { laneMatches },
        rowsSelected,
        isBulkMatchingOperationEnabled
      },
      featureFlags: { returnICMSummaryFromUpsertEndpoint }
    } = getState();

    dispatch(setSaveCarrierMatchDetails({ isFetching: true, isError: false }));

    try {
      const laneCarrierCapacityMatches = mapLaneCarrierCapacityMatch(
        capacityMatches,
        rowsSelected,
        isNoneOptionSelected,
        isBulkMatchingOperationEnabled ? updatedLaneMatches : laneMatches,
        clearExistingMatches
      );

      if (returnICMSummaryFromUpsertEndpoint) {
        let updatedLanesSummary = (
          await upsertLaneCarrierCapacityMatches(
            bidId,
            laneCarrierCapacityMatches,
            user.userRoleForManagementUser
          )
        ).data;

        dispatch(
          receiveCapacityMatchLaneSummarySuccessForEditedLanes(
            updatedLanesSummary
          )
        );
        dispatch(
          setSaveCarrierMatchDetails({ isFetching: false, isError: false })
        );
        callbackFn && callbackFn();
      } else {
        await upsertLaneCarrierCapacityMatches(
          bidId,
          laneCarrierCapacityMatches,
          user.userRoleForManagementUser
        );

        dispatch(
          setSaveCarrierMatchDetails({ isFetching: false, isError: false })
        );
        callbackFn && callbackFn();
        dispatch(fetchCapacityMatchLaneSummary(bidId, false));
      }
    } catch {
      dispatch(
        setSaveCarrierMatchDetails({ isFetching: false, isError: true })
      );
    }
  };

export const setCapacityMatchStatus =
  (bidId, bidModeId, capacityMatchingStatus, sendNotifications, role) =>
  async (dispatch, getState) => {
    const {
      user: { user }
    } = getState();

    dispatch(requestUpdateCapacityMatchStatus());

    try {
      await updateCapacityMatchingStatus(
        bidId,
        bidModeId,
        capacityMatchingStatus,
        sendNotifications,
        user.userRoleForManagementUser
      );
      dispatch(receiveUpdateCapacityMatchStatusSuccess());
      dispatch(fetchBidSummary({ bidId, role }));
    } catch {
      dispatch(receiveUpdateCapacityMatchStatusFailure());
    }
  };

export const checkCapacityMatchingInProgress =
  (bidId, callbackSuccess, callbackError) => async (dispatch, getState) => {
    const {
      user: { userRoleForManagementUser }
    } = getState().user;

    try {
      const value = (
        await getIsCapacityMatchingInProgress(bidId, userRoleForManagementUser)
      ).data;
      dispatch(setCapacityMatchingInProgress(value));
      callbackSuccess && callbackSuccess(value);
    } catch {
      dispatch(setCapacityMatchingInProgress(false));
      callbackError && callbackError();
    }
  };

export const refreshCapacityMatches =
  (bidId, role) => async (dispatch, getState) => {
    const {
      user: { userRoleForManagementUser }
    } = getState().user;

    dispatch(setRefreshCapacityMatchesIsLoading(true));

    try {
      await runInternalCapacityMatching(bidId, userRoleForManagementUser);
      dispatch(setRefreshCapacityMatchesIsLoading(false));
      dispatch(checkCapacityMatchingInProgress(bidId));
      dispatch(fetchBidSummary({ bidId, role }));
    } catch {
      dispatch(setRefreshCapacityMatchesIsLoading(false));
    }
  };

export const getMatchingLastRunDate = bidId => async (dispatch, getState) => {
  const {
    user: { userRoleForManagementUser }
  } = getState().user;

  try {
    const matchingRunDate = (
      await getMatchingRunDate(bidId, userRoleForManagementUser)
    ).data;
    const differenceBetweenMatchesLastRan = differenceInDays(
      new Date(),
      parseISO(matchingRunDate)
    );
    dispatch(setMatchesLastRanBefore7Days(differenceBetweenMatchesLastRan > 7));
  } catch {
    dispatch(setMatchesLastRanBefore7Days(false));
  }
};

export const fetchRateHistory =
    (bidId, truckloadLaneId, callbackFn) => async (dispatch) => {
        dispatch(requestRateHistory());

        try {
            let rateHistory = (
                await getRateHistory(
                    truckloadLaneId,
                    bidId
                )
            ).data;
            dispatch(
                receiveRateHistorySuccess({ rateHistory })
            );
            callbackFn && callbackFn();
        } catch {
            dispatch(receiveRateHistoryFailure());
        }
    };